// TrainingAnalytics.jsx
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Spinner, Form, Modal } from 'react-bootstrap';
import {
    BarChart3,
    TrendingUp,
    Users,
    Award,
    Calendar,
    Download,
    Filter,
    Eye,
    GraduationCap,
    Clock,
    Target,
    BookOpen,
    ChevronRight,
    PieChart,
    Activity
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from "../AuthContext/AuthContext";
import { getAuthenticatedAxios } from '../Utilities/axiosConfiguration';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Mandatory training list - these are the required trainings for compliance
// Note: These are official course names and should not be translated
const TRAINING_LIST = [
    'Anti-fraud e-learning: Fraud and Corruption Awareness and Prevention',
    'BSAFE',
    'Ethics and Integrity at UNDP',
    'Preventing Sexual Harassment and Other Prohibited Conduct',
    'Prevention of Sexual Exploitation and Abuse of the Local Population (PSEA)',
    'Individual First Aid Kit (IFAK) Course',
    'Safe and Secure Approaches in Field Environments (SSAFE)'
];

const REQUIRED_TRAININGS_COUNT = TRAINING_LIST.length; // 7 mandatory trainings

// Analytics Card Component
const AnalyticsCard = ({ icon: Icon, title, value, subtitle, trend, color = "primary", onClick }) => {
    const { t } = useTranslation(['trainingAnalytics']); // Try array syntax
    // Alternative: const { t } = useTranslation('trainingAnalytics'); then use t('trainingAnalytics:key')

    return (
        <Card
            className={`border-0 shadow-sm h-100 analytics-card ${onClick ? 'cursor-pointer' : ''}`}
            onClick={onClick}
        >
            <Card.Body className="p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className={`bg-${color} bg-opacity-10 p-3 rounded-circle`}>
                        <Icon size={24} className={`text-${color}`} />
                    </div>
                    {trend && (
                        <Badge bg={trend > 0 ? 'success' : 'danger'} className="px-2 py-1">
                            <TrendingUp size={12} className="me-1" />
                            {trend > 0 ? '+' : ''}{trend}%
                        </Badge>
                    )}
                </div>
                <h3 className="fw-bold mb-1">{value}</h3>
                <p className="text-muted mb-1 fw-semibold">{title}</p>
                {subtitle && <small className="text-muted">{subtitle}</small>}
                {onClick && (
                    <div className="mt-2">
                        <small className="text-primary fw-semibold">
                            {t('common.viewDetails')} <ChevronRight size={12} className="ms-1" />
                        </small>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

// Report Section Component
const ReportSection = ({ title, description, children, icon: Icon }) => {
    return (
        <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-light border-0 py-3">
                <div className="d-flex align-items-center">
                    <Icon size={20} className="text-primary me-2" />
                    <div>
                        <h5 className="mb-1 fw-bold">{title}</h5>
                        <small className="text-muted">{description}</small>
                    </div>
                </div>
            </Card.Header>
            <Card.Body className="p-4">
                {children}
            </Card.Body>
        </Card>
    );
};

// Detailed Report Modal
const ReportModal = ({ show, onHide, reportType, data, t }) => {
    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title className="d-flex align-items-center">
                    <BarChart3 size={24} className="me-2" />
                    {t(`reports.${reportType}.title`)}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="table-responsive">
                    <Table striped hover>
                        <thead>
                        <tr>
                            {reportType === 'completion' && (
                                <>
                                    <th>{t('reports.table.staff')}</th>
                                    <th>{t('reports.table.training')}</th>
                                    <th>{t('reports.table.status')}</th>
                                    <th>{t('reports.table.completionDate')}</th>
                                </>
                            )}
                            {reportType === 'compliance' && (
                                <>
                                    <th>{t('reports.table.unit')}</th>
                                    <th>{t('reports.table.requiredTrainings')}</th>
                                    <th>{t('reports.table.completed')}</th>
                                    <th>{t('reports.table.pending')}</th>
                                    <th>{t('reports.table.complianceRate')}</th>
                                </>
                            )}
                            {reportType === 'trends' && (
                                <>
                                    <th>{t('reports.table.month')}</th>
                                    <th>{t('reports.table.completions')}</th>
                                    <th>{t('reports.table.newEnrollments')}</th>
                                    <th>{t('reports.table.growthRate')}</th>
                                </>
                            )}
                        </tr>
                        </thead>
                        <tbody>
                        {data?.map((item, index) => (
                            <tr key={index}>
                                {reportType === 'completion' && (
                                    <>
                                        <td className="fw-semibold">{item.staffName}</td>
                                        <td>{item.trainingName}</td>
                                        <td>
                                            <Badge bg={item.status === t('completed') ? 'success' : 'warning'}>
                                                {item.status}
                                            </Badge>
                                        </td>
                                        <td>{item.completionDate || 'N/A'}</td>
                                    </>
                                )}
                                {reportType === 'compliance' && (
                                    <>
                                        <td className="fw-semibold">{item.unit}</td>
                                        <td>{item.requiredTrainings}</td>
                                        <td>
                                            <Badge bg="success">{item.completed}</Badge>
                                        </td>
                                        <td>
                                            <Badge bg="warning">{item.pending || (item.requiredTrainings - item.completed)}</Badge>
                                        </td>
                                        <td>
                                            <Badge bg={item.complianceRate >= 80 ? 'success' : item.complianceRate >= 60 ? 'warning' : 'danger'}>
                                                {item.complianceRate}%
                                            </Badge>
                                        </td>
                                    </>
                                )}
                                {reportType === 'trends' && (
                                    <>
                                        <td className="fw-semibold">{item.month}</td>
                                        <td>{item.completions}</td>
                                        <td>{item.enrollments}</td>
                                        <td>
                                            <Badge bg={item.growthRate > 0 ? 'success' : 'danger'}>
                                                {item.growthRate > 0 ? '+' : ''}{item.growthRate}%
                                            </Badge>
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </div>
            </Modal.Body>
        </Modal>
    );
};

// Main Training Analytics Component
const TrainingAnalytics = () => {
    const { t } = useTranslation('trainingAnalytics');
    const [loading, setLoading] = useState(true);
    const [analyticsData, setAnalyticsData] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [modalReportType, setModalReportType] = useState('');
    const [modalData, setModalData] = useState([]);
    const [dateRange, setDateRange] = useState('30');

    const { isLoggedIn, logout } = useAuth();
    const axios = getAuthenticatedAxios();

    useEffect(() => {
        if (isLoggedIn) {
            fetchAnalyticsData();
        }
    }, [isLoggedIn, dateRange]);

    const fetchAnalyticsData = async () => {
        try {
            console.log(t('fetchingAnalyticsData'));

            // Try different endpoint combinations based on your existing API structure
            let usersResponse, allTrainingsResponse;

            try {
                // First, try to get all users - you might need to adjust this endpoint
                usersResponse = await axios.get('/users/list');
            } catch (error) {
                console.warn('Failed to fetch from /users/list, trying alternative endpoints:', error);
                // Fallback: try getting staff information list
                try {
                    usersResponse = await axios.get('/staff_information/list');
                } catch (staffError) {
                    console.warn(t('failedToFetchStaffInformation'), staffError);
                    usersResponse = { data: [] };
                }
            }

            try {
                // Try to get all training records - this endpoint might not exist yet
                allTrainingsResponse = await axios.get('/training/all');
            } catch (error) {
                console.warn('Failed to fetch from /training/all, this endpoint might not exist:', error);
                // Fallback: just get current user's training for now
                try {
                    allTrainingsResponse = await axios.get('/training/me');
                } catch (trainingError) {
                    console.warn(t('failedToFetchTrainingData'), trainingError);
                    allTrainingsResponse = { data: [] };
                }
            }

            const users = usersResponse.data || [];
            let trainings = allTrainingsResponse.data || [];

            console.log(t('fetchedData'), {
                usersCount: users.length,
                trainingsCount: trainings.length,
                sampleUser: users[0],
                sampleTraining: trainings[0]
            });

            // Apply date range filter based on year_completed
            const currentYear = new Date().getFullYear();
            const daysFilter = parseInt(dateRange);

            console.log(t('applyingDateFilter'), { dateRange, daysFilter, currentYear });

            // Filter trainings based on date range
            if (daysFilter <= 90) {
                // For short ranges (7, 30, 90 days), show only current year
                trainings = trainings.filter(t => {
                    if (t.year_completed) {
                        return t.year_completed === currentYear;
                    }
                    // If no year_completed, assume it's current (incomplete trainings)
                    return true;
                });
            } else if (daysFilter === 365) {
                // For t('lastYear'), show current year and previous year
                trainings = trainings.filter(t => {
                    if (t.year_completed) {
                        return t.year_completed >= currentYear - 1;
                    }
                    return true;
                });
            }

            console.log(t('trainingsAfterDateFilter'), trainings.length);

            // Calculate overview metrics
            const totalStaff = users.length;
            const totalTrainings = trainings.length > 0 ?
                [...new Set(trainings.map(t => t.training_name))].length : 0;

            // Group trainings by user to calculate completion rates
            const trainingsByUser = trainings.reduce((acc, training) => {
                if (!acc[training.user_id]) {
                    acc[training.user_id] = [];
                }
                acc[training.user_id].push(training);
                return acc;
            }, {});

            console.log(t('trainingsByUser'), trainingsByUser);

            // Calculate completion rate based on training_status
            const completedTrainings = trainings.filter(t => {
                const isCompleted = t.training_status === t('completed') ||
                    t.training_status === 'completed' ||
                    t.training_status === t('completed') ||
                    Boolean(t.year_completed);
                console.log(`Training ${t.training_name}: status=${t.training_status}, year=${t.year_completed}, isCompleted=${isCompleted}`);
                return isCompleted;
            });

            console.log(t('completedTrainings'), completedTrainings.length, 'out of', trainings.length);

            // Check which trainings are mandatory
            const mandatoryCompletedTrainings = completedTrainings.filter(t =>
                TRAINING_LIST.some(mandatoryTraining =>
                    t.training_name?.toLowerCase().includes(mandatoryTraining.toLowerCase()) ||
                    mandatoryTraining.toLowerCase().includes(t.training_name?.toLowerCase())
                )
            );

            console.log(t('mandatoryTrainingsCompleted'), mandatoryCompletedTrainings.length, 'out of', REQUIRED_TRAININGS_COUNT, 'required (7 mandatory trainings)');
            console.log(t('nonmandatoryTrainingsCompleted'), completedTrainings.length - mandatoryCompletedTrainings.length);

            // Log which mandatory trainings are present
            const mandatoryTrainingNames = mandatoryCompletedTrainings.map(t => t.training_name);
            console.log(t('completedMandatoryTrainings'), mandatoryTrainingNames);

            const completionRate = trainings.length > 0 ?
                Math.round((completedTrainings.length / trainings.length) * 100) : 0;

            // Calculate compliance rate (staff with at least 1 training vs total staff)
            const staffWithTraining = Object.keys(trainingsByUser).length;
            const complianceRate = totalStaff > 0 ?
                Math.round((staffWithTraining / totalStaff) * 100) : 0;

            console.log(t('analyticsCalculations'), {
                totalStaff,
                totalTrainings,
                completionRate,
                complianceRate,
                staffWithTraining,
                requiredTrainingsPerStaff: REQUIRED_TRAININGS_COUNT,
                totalRequiredTrainings: totalStaff * REQUIRED_TRAININGS_COUNT
            });

            // Create completion data for recent completions
            // First, deduplicate trainings based on user_id + training_name + year_completed
            const uniqueCompletedTrainings = completedTrainings.reduce((acc, training) => {
                const key = `${training.user_id}_${training.training_name}_${training.year_completed || 'unknown'}`;
                // Keep the one with the highest ID (most recent entry)
                if (!acc[key] || training.id > acc[key].id) {
                    acc[key] = training;
                }
                return acc;
            }, {});

            const recentCompletions = Object.values(uniqueCompletedTrainings)
                .sort((a, b) => {
                    // Sort by year_completed if available, otherwise by id (most recent first)
                    if (a.year_completed && b.year_completed) {
                        return b.year_completed - a.year_completed;
                    }
                    return b.id - a.id;
                })
                .slice(0, 10)
                .map(training => {
                    const user = users.find(u => u.id === training.user_id);
                    return {
                        staffName: user ?
                            `${user.first_name || user.firstName || ''} ${user.last_name || user.lastName || ''}`.trim() :
                            t('common.unknown'),
                        trainingName: training.training_name || t('common.unknownTraining'),
                        status: t('filters.completed'),
                        completionDate: training.year_completed ?
                            `${training.year_completed}` :
                            new Date().getFullYear().toString()
                    };
                });

            // Create compliance data by unit
            const unitCompliance = {};

            // Try to get employment data for users, but handle if it fails
            const usersWithEmployment = await Promise.all(
                users.map(async (user) => {
                    try {
                        const employmentResponse = await axios.get(`/employment/${user.id}`);
                        return { ...user, employment: employmentResponse.data };
                    } catch (error) {
                        console.warn(`Could not fetch employment for user ${user.id}:`, error);
                        return { ...user, employment: null };
                    }
                })
            );

            usersWithEmployment.forEach(user => {
                // Try multiple possible field names for unit
                const unit = user.employment?.unit ||
                    user.employment?.unit_name ||
                    user.employment?.department ||
                    user.unit ||
                    user.unit_name ||
                    user.department ||
                    t('missingEmployment');

                // Debug logging for unit identification
                if (unit === t('missingEmployment')) {
                    console.log(t('userWithNoUnitFound'), {
                        userId: user.id,
                        userName: `${user.first_name || user.firstName} ${user.last_name || user.lastName}`,
                        employment: user.employment,
                        userFields: Object.keys(user)
                    });
                }

                if (!unitCompliance[unit]) {
                    unitCompliance[unit] = {
                        unit,
                        totalStaff: 0,
                        staffWithTraining: 0,
                        requiredTrainings: REQUIRED_TRAININGS_COUNT, // 12 mandatory trainings
                        completed: 0
                    };
                }

                unitCompliance[unit].totalStaff++;

                const userTrainings = trainingsByUser[user.id] || [];
                if (userTrainings.length > 0) {
                    unitCompliance[unit].staffWithTraining++;
                }

                // Only count completed trainings that are in the mandatory TRAINING_LIST
                unitCompliance[unit].completed += userTrainings.filter(t => {
                    const isCompleted = t.training_status === t('completed') ||
                        t.training_status === 'completed' ||
                        t.training_status === t('completed') ||
                        Boolean(t.year_completed);

                    // Check if this training is in the mandatory list
                    const isMandatory = TRAINING_LIST.some(mandatoryTraining =>
                        t.training_name?.toLowerCase().includes(mandatoryTraining.toLowerCase()) ||
                        mandatoryTraining.toLowerCase().includes(t.training_name?.toLowerCase())
                    );

                    return isCompleted && isMandatory;
                }).length;
            });

            const complianceData = Object.values(unitCompliance).map(unit => {
                // Calculate two types of compliance:
                // 1. Participation rate: % of staff who have at least one training
                const participationRate = unit.totalStaff > 0 ?
                    Math.round((unit.staffWithTraining / unit.totalStaff) * 100) : 0;

                // 2. Completion rate: % of required trainings that are completed
                const totalRequired = unit.requiredTrainings * unit.totalStaff;
                const completionRate = totalRequired > 0 ?
                    Math.round((unit.completed / totalRequired) * 100) : 0;

                return {
                    ...unit,
                    // Use completion rate as the primary metric (more meaningful)
                    complianceRate: completionRate,
                    participationRate: participationRate, // Keep for reference
                    pending: totalRequired - unit.completed
                };
            });

            // Log compliance data for debugging
            console.log(t('complianceByUnit'), complianceData.map(u => ({
                unit: u.unit,
                staff: u.totalStaff,
                required: `${u.requiredTrainings} × ${u.totalStaff} = ${u.totalStaff * u.requiredTrainings}`,
                completed: u.completed,
                rate: `${u.complianceRate}%`,
                pending: u.pending
            })));

            // Create trends data based on actual completion data
            const trendsData = [];
            const currentDate = new Date();

            for (let i = 5; i >= 0; i--) {
                const month = new Date(currentDate);
                month.setMonth(month.getMonth() - i);
                const monthName = month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                const monthYear = month.getFullYear();

                // Count completions for this specific month/year
                const monthCompletions = completedTrainings.filter(t => {
                    if (t.year_completed) {
                        return t.year_completed === monthYear;
                    }
                    return monthYear === currentDate.getFullYear();
                }).length;

                // Count all training records (completed + incomplete) for this year
                const enrollments = trainings.filter(t => {
                    if (t.year_completed) {
                        return t.year_completed === monthYear;
                    }
                    // If no year_completed, assume it's current year
                    return monthYear === currentDate.getFullYear();
                }).length;

                const completions = Math.max(1, Math.round(monthCompletions / (i === 0 ? 1 : 2)));

                const prevData = trendsData[trendsData.length - 1];
                const growthRate = prevData && prevData.completions > 0 ?
                    Math.round(((completions - prevData.completions) / prevData.completions) * 100) :
                    0;

                trendsData.push({
                    month: monthName,
                    completions: completions,
                    enrollments: enrollments,
                    growthRate: growthRate
                });
            }

            // Popular trainings analysis based on actual data
            const trainingCounts = trainings.reduce((acc, training) => {
                const name = training.training_name || t('common.unknown');
                acc[name] = (acc[name] || 0) + 1;
                return acc;
            }, {});

            const popularTrainings = Object.entries(trainingCounts)
                .sort(([,a], [,b]) => b - a)
                .slice(0, 5)
                .map(([name, count]) => ({ name, enrollments: count }));

            const finalAnalyticsData = {
                overview: {
                    totalStaff,
                    totalTrainings,
                    completionRate,
                    complianceRate
                },
                completion: recentCompletions,
                compliance: complianceData,
                trends: trendsData,
                popularTrainings
            };

            console.log(t('finalAnalyticsData'), finalAnalyticsData);
            setAnalyticsData(finalAnalyticsData);
            setLoading(false);

        } catch (error) {
            console.error(t('errorFetchingAnalyticsData'), error);
            if (error.response?.status === 401) logout();

            // Fallback to empty data structure
            setAnalyticsData({
                overview: { totalStaff: 0, totalTrainings: 0, completionRate: 0, complianceRate: 0 },
                completion: [],
                compliance: [],
                trends: [],
                popularTrainings: []
            });
            setLoading(false);
        }
    };

    const handleReportClick = (reportType, data) => {
        setModalReportType(reportType);
        setModalData(data);
        setShowModal(true);
    };

    const exportReport = (reportType, format) => {
        const data = analyticsData[reportType] || [];

        if (data.length === 0) {
            alert(t('common.noData'));
            return;
        }

        if (format === 'excel') {
            let exportData = [];

            if (reportType === 'completion') {
                exportData = data.map(item => ({
                    [t('reports.table.staff')]: item.staffName,
                    [t('reports.table.training')]: item.trainingName,
                    [t('reports.table.status')]: item.status,
                    [t('reports.table.completionDate')]: item.completionDate || 'N/A'
                }));
            } else if (reportType === 'compliance') {
                exportData = data.map(item => ({
                    [t('reports.table.unit')]: item.unit,
                    [t('reports.table.required')]: item.requiredTrainings,
                    [t('reports.table.completed')]: item.completed,
                    [t('reports.table.pending')]: item.pending,
                    [t('reports.table.complianceRate')]: `${item.complianceRate}%`
                }));
            } else if (reportType === 'trends') {
                exportData = data.map(item => ({
                    [t('reports.table.month')]: item.month,
                    [t('reports.table.completions')]: item.completions,
                    [t('reports.table.enrollments')]: item.enrollments,
                    [t('reports.table.growthRate')]: `${item.growthRate}%`
                }));
            }

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, t(`reports.${reportType}.title`));
            XLSX.writeFile(workbook, `training_${reportType}_report_${new Date().toISOString().split('T')[0]}.xlsx`);

        } else if (format === 'pdf') {
            const doc = new jsPDF();
            doc.setFontSize(18);
            doc.text(t(`reports.${reportType}.title`), 14, 20);
            doc.setFontSize(12);
            doc.text(`${t('common.export')}: ${new Date().toLocaleDateString()}`, 14, 30);

            let tableData = [];
            let headers = [];

            if (reportType === 'completion') {
                headers = [t('reports.table.staff'), t('reports.table.training'), t('reports.table.status'), t('reports.table.completionDate')];
                tableData = data.map(item => [item.staffName, item.trainingName, item.status, item.completionDate || 'N/A']);
            } else if (reportType === 'compliance') {
                headers = [t('reports.table.unit'), t('reports.table.required'), t('reports.table.completed'), t('reports.table.rate')];
                tableData = data.map(item => [item.unit, item.requiredTrainings, item.completed, `${item.complianceRate}%`]);
            } else if (reportType === 'trends') {
                headers = [t('reports.table.month'), t('reports.table.completions'), t('reports.table.enrollments'), t('reports.table.growth')];
                tableData = data.map(item => [item.month, item.completions, item.enrollments, `${item.growthRate}%`]);
            }

            doc.autoTable({
                head: [headers],
                body: tableData,
                startY: 40,
                styles: { fontSize: 9, cellPadding: 3 },
                headStyles: { fillColor: [0, 120, 212], fontSize: 10 }
            });

            doc.save(`training_${reportType}_report_${new Date().toISOString().split('T')[0]}.pdf`);
        }
    };

    if (!isLoggedIn) {
        return (
            <Container className="mt-5 text-center">
                <Card className="mx-auto" style={{ maxWidth: '400px' }}>
                    <Card.Body className="py-5">
                        <BarChart3 size={48} className="text-muted mb-3" />
                        <h5>{t('auth.loginRequired')}</h5>
                        <p className="text-muted">{t('auth.loginMessage')}</p>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    return (
        <div className="min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
            <Container fluid className="py-3 px-3">
                <div className="mx-auto" style={{ maxWidth: '1600px' }}>
                    {/* Page Header */}
                    <Card className="mb-4 shadow-sm border-0 overflow-hidden">
                        <div className="bg-gradient-primary text-white py-4 px-4">
                            <Row className="align-items-center">
                                <Col>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-white bg-opacity-25 p-3 rounded-circle me-4">
                                            <BarChart3 size={32} className="text-white" />
                                        </div>
                                        <div>
                                            <h2 className="mb-2 fw-bold text-white">{t('trainingAnalytics:title')}</h2>
                                            <p className="mb-0 text-white-50 fs-6">{t('trainingAnalytics:subtitle')}</p>
                                        </div>
                                    </div>
                                </Col>
                                <Col xs="auto" className="d-print-none">
                                    <Form.Select
                                        value={dateRange}
                                        onChange={(e) => setDateRange(e.target.value)}
                                        className="bg-white text-primary fw-semibold"
                                        size="sm"
                                    >
                                        <option value="7">{t('dateRange.last7Days')}</option>
                                        <option value="30">{t('dateRange.last30Days')}</option>
                                        <option value="90">{t('dateRange.last90Days')}</option>
                                        <option value="365">{t('dateRange.lastYear')}</option>
                                    </Form.Select>
                                </Col>
                            </Row>
                        </div>
                    </Card>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" size="lg" />
                            <div className="mt-3 text-muted h6">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            {/* Overview Cards */}
                            <Row className="mb-4">
                                <Col md={3} className="mb-3">
                                    <AnalyticsCard
                                        icon={Users}
                                        title={t('overview.totalStaff')}
                                        value={analyticsData.overview?.totalStaff || 0}
                                        subtitle={t('overview.activeMembers')}
                                        color="primary"
                                    />
                                </Col>
                                <Col md={3} className="mb-3">
                                    <AnalyticsCard
                                        icon={GraduationCap}
                                        title={t('overview.totalTrainings')}
                                        value={analyticsData.overview?.totalTrainings || 0}
                                        subtitle={t('overview.availableCourses')}
                                        color="success"
                                    />
                                </Col>
                                <Col md={3} className="mb-3">
                                    <AnalyticsCard
                                        icon={Award}
                                        title={t('overview.completionRate')}
                                        value={`${analyticsData.overview?.completionRate || 0}%`}
                                        subtitle={t('overview.thisMonth')}
                                        trend={5.2}
                                        color="warning"
                                    />
                                </Col>
                                <Col md={3} className="mb-3">
                                    <AnalyticsCard
                                        icon={Target}
                                        title={t('overview.complianceRate')}
                                        value={`${analyticsData.overview?.complianceRate || 0}%`}
                                        subtitle={t('overview.organizationWide')}
                                        trend={-2.1}
                                        color="info"
                                    />
                                </Col>
                            </Row>

                            {/* Training Completion Reports */}
                            <ReportSection
                                title={t('reports.completion.title')}
                                description={t('reports.completion.description')}
                                icon={Award}
                            >
                                <Row>
                                    <Col md={8}>
                                        <div className="mb-3">
                                            <h6 className="fw-semibold mb-2">{t('reports.recentCompletions')}</h6>
                                            <div className="table-responsive">
                                                <Table size="sm" hover>
                                                    <thead>
                                                    <tr>
                                                        <th>{t('reports.table.staff')}</th>
                                                        <th>{t('reports.table.training')}</th>
                                                        <th>{t('reports.table.date')}</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {analyticsData.completion?.slice(0, 5).map((item, index) => (
                                                        <tr key={index}>
                                                            <td className="fw-semibold">{item.staffName}</td>
                                                            <td>{item.trainingName}</td>
                                                            <td>{item.completionDate}</td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="d-grid gap-2">
                                            <Button
                                                variant="outline-primary"
                                                onClick={() => handleReportClick('completion', analyticsData.completion)}
                                            >
                                                <Eye size={16} className="me-2" />
                                                {t('reports.viewFullReport')}
                                            </Button>
                                            <Button
                                                variant="outline-success"
                                                onClick={() => exportReport('completion', 'excel')}
                                            >
                                                <Download size={16} className="me-2" />
                                                {t('reports.exportExcel')}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </ReportSection>

                            {/* Compliance Reports */}
                            <ReportSection
                                title={t('reports.compliance.title')}
                                description={t('reports.compliance.description')}
                                icon={Target}
                            >
                                <Row>
                                    <Col md={8}>
                                        <div className="mb-3">
                                            <h6 className="fw-semibold mb-2">{t('reports.complianceByUnit')}</h6>
                                            <div className="table-responsive">
                                                <Table size="sm" hover>
                                                    <thead>
                                                    <tr>
                                                        <th>{t('reports.table.unit')}</th>
                                                        <th>{t('reports.table.required')}</th>
                                                        <th>{t('reports.table.completed')}</th>
                                                        <th>{t('reports.table.rate')}</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {analyticsData.compliance?.slice(0, 5).map((item, index) => (
                                                        <tr key={index}>
                                                            <td className="fw-semibold">{item.unit}</td>
                                                            <td>{item.requiredTrainings}</td>
                                                            <td>{item.completed}</td>
                                                            <td>
                                                                <Badge bg={item.complianceRate >= 80 ? 'success' : item.complianceRate >= 60 ? 'warning' : 'danger'}>
                                                                    {item.complianceRate}%
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="d-grid gap-2">
                                            <Button
                                                variant="outline-primary"
                                                onClick={() => handleReportClick('compliance', analyticsData.compliance)}
                                            >
                                                <Eye size={16} className="me-2" />
                                                {t('reports.viewFullReport')}
                                            </Button>
                                            <Button
                                                variant="outline-success"
                                                onClick={() => exportReport('compliance', 'excel')}
                                            >
                                                <Download size={16} className="me-2" />
                                                {t('reports.exportExcel')}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </ReportSection>

                            {/* Training Trends */}
                            <ReportSection
                                title={t('reports.trends.title')}
                                description={t('reports.trends.description')}
                                icon={TrendingUp}
                            >
                                <Row>
                                    <Col md={8}>
                                        <div className="mb-3">
                                            <h6 className="fw-semibold mb-2">{t('reports.monthlyTrends')}</h6>
                                            <div className="table-responsive">
                                                <Table size="sm" hover>
                                                    <thead>
                                                    <tr>
                                                        <th>{t('reports.table.month')}</th>
                                                        <th>{t('reports.table.completions')}</th>
                                                        <th>{t('reports.table.enrollments')}</th>
                                                        <th>{t('reports.table.growth')}</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {analyticsData.trends?.slice(0, 6).map((item, index) => (
                                                        <tr key={index}>
                                                            <td className="fw-semibold">{item.month}</td>
                                                            <td>{item.completions}</td>
                                                            <td>{item.enrollments}</td>
                                                            <td>
                                                                <Badge bg={item.growthRate > 0 ? 'success' : 'danger'}>
                                                                    {item.growthRate > 0 ? '+' : ''}{item.growthRate}%
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="d-grid gap-2">
                                            <Button
                                                variant="outline-primary"
                                                onClick={() => handleReportClick('trends', analyticsData.trends)}
                                            >
                                                <Eye size={16} className="me-2" />
                                                {t('reports.viewFullReport')}
                                            </Button>
                                            <Button
                                                variant="outline-success"
                                                onClick={() => exportReport('trends', 'excel')}
                                            >
                                                <Download size={16} className="me-2" />
                                                {t('reports.exportExcel')}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </ReportSection>

                            {/* Training Categories Analysis */}
                            <ReportSection
                                title={t('reports.categories.title')}
                                description={t('reports.categories.description')}
                                icon={BookOpen}
                            >
                                <Row>
                                    <Col md={12} className="mb-3">
                                        <h6 className="fw-semibold mb-3">{t('reports.popularTrainings')}</h6>
                                        {analyticsData.popularTrainings?.length > 0 ? (
                                            analyticsData.popularTrainings.map((training, index) => (
                                                <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
                                                    <span className="fw-semibold">{training.name}</span>
                                                    <Badge bg="primary">{training.enrollments} {t('reports.enrollments')}</Badge>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-center text-muted py-3">
                                                <BookOpen size={32} className="mb-2 opacity-50" />
                                                <p>{t('common.noData')}</p>
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                            </ReportSection>
                        </>
                    )}

                    <ReportModal
                        show={showModal}
                        onHide={() => setShowModal(false)}
                        reportType={modalReportType}
                        data={modalData}
                        t={t}
                    />
                </div>
            </Container>

            <style jsx>{`
                .bg-gradient-primary {
                    background: linear-gradient(135deg, #0066cc 0%, #004499 100%) !important;
                }
                
                .analytics-card {
                    transition: all 0.3s ease;
                }
                
                .analytics-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
                }
                
                .cursor-pointer {
                    cursor: pointer;
                }

                @media print {
                    .d-print-none { display: none !important; }
                }
            `}</style>
        </div>
    );
};

export default TrainingAnalytics;